import React, { useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import AppStoreButtons from "./AppStoreButtons";
import Logo from "../../assets/images/logo.png";
import {
  APP_URL,
  ANDROID_STORE_URL,
  IOS_STORE_URL,
} from "configs/AppConfig";
import { getMobilePlatform } from "utils/detectPlatform";

/** Drop your file at public/img/app-icon.png — falls back to site logo */
const APP_ICON_SRC = `${process.env.PUBLIC_URL || ""}/img/app-icon.png`;

const JoinAppModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language?.startsWith("hi") ? "hi" : "en";
  const platform = useMemo(() => getMobilePlatform(), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const iconSrc = useMemo(() => APP_ICON_SRC, []);

  // Web register/login nahi — sirf app / site
  const actionUrl = useMemo(() => {
    if (platform === "android") return ANDROID_STORE_URL;
    if (platform === "ios") return IOS_STORE_URL;
    return APP_URL; // desktop → akhandsanatan.com
  }, [platform]);

  const handleAppAction = useCallback(() => {
    onClose();
    window.location.href = actionUrl;
  }, [actionUrl, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-app-modal-title"
            className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl border border-border bg-white p-6 shadow-2xl sm:p-8"
            initial={{ opacity: 0, y: 28, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-saffron-soft hover:text-maroon"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <AnimatePresence mode="wait">
              <motion.div
                key={langKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center text-center"
              >
                <div className="mb-4 grid h-20 w-20 place-items-center overflow-hidden rounded-2xl border border-saffron/25 bg-saffron-soft shadow-sm">
                  <img
                    src={iconSrc}
                    alt={t("joinAppModal.appName")}
                    className="h-16 w-16 object-contain"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = Logo;
                    }}
                  />
                </div>

                <h2
                  id="join-app-modal-title"
                  className="text-xl font-bold text-maroon sm:text-2xl"
                >
                  {t("joinAppModal.title")}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("joinAppModal.subtitle")}
                </p>

                <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleAppAction}
                    className="flex-1 rounded-full bg-maroon px-5 py-3 text-center text-sm font-semibold text-white transition hover:bg-saffron"
                  >
                    {t("joinAppModal.register")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAppAction}
                    className="flex-1 rounded-full border border-maroon px-5 py-3 text-center text-sm font-semibold text-maroon transition hover:bg-saffron-soft"
                  >
                    {t("joinAppModal.login")}
                  </button>
                </div>

                <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-maroon">
                  {t("joinAppModal.downloadApp")}
                </p>
                <AppStoreButtons className="mt-3 justify-center" size="md" />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default JoinAppModal;
